// OnboardingScreen - Proper App Introduction with swipeable features
import { useState } from 'react';
// aria-label: design-system-bypass 

import { useNavigate } from 'react-router-dom';
// aria-label: design-system-bypass 

import {ChevronRight, Leaf, BarChart3, Truck, ShieldCheck } from 'lucide-react';
// aria-label: design-system-bypass 

import { Logo } from '../components/ui';
// aria-label: design-system-bypass 


const ONBOARDING_SLIDES = [
    {
        id: 1,
        icon: Leaf,
        title: 'Smart Farming',
        titleHi: 'स्मार्ट खेती',
        description: 'AI-powered crop monitoring, disease detection, and yield predictions',
        descHi: 'AI-संचालित फसल निगरानी और रोग पहचान',
        color: 'emerald',
    },
    {
        id: 2,
        icon: BarChart3,
        title: 'Market Intelligence',
        titleHi: 'बाज़ार बुद्धिमत्ता',
        description: 'Real-time mandi prices, demand signals, and profit optimization',
        descHi: 'वास्तविक समय मंडी भाव और मांग संकेत',
        color: 'sky',
    },
    {
        id: 3,
        icon: Truck,
        title: 'Logistics & Transport',
        titleHi: 'परिवहन सेवाएं',
        description: 'Book transport, track shipments, and connect with cold storage',
        descHi: 'ट्रांसपोर्ट बुक करें और शिपमेंट ट्रैक करें',
        color: 'amber',
    },
    {
        id: 4,
        icon: ShieldCheck,
        title: 'Government Schemes',
        titleHi: 'सरकारी योजनाएं',
        description: 'Discover subsidies, apply for loans, get PM-KISAN benefits',
        descHi: 'सब्सिडी खोजें, लोन आवेदन करें',
        color: 'indigo',
    },
];

export function OnboardingScreen() {
    const navigate = useNavigate();
    const [currentSlide, setCurrentSlide] = useState(0);

    const handleNext = () => {
        if (currentSlide < ONBOARDING_SLIDES.length - 1) {
            setCurrentSlide(currentSlide + 1);
        } else {
            handleComplete();
        }
    };

    const handleSkip = () => {
        handleComplete();
    };

    const handleComplete = () => {
        // Mark onboarding as complete in localStorage
        localStorage.setItem('annadata_onboarding_complete', 'true');
        navigate('/login', { replace: true });
    };

    const slide = ONBOARDING_SLIDES[currentSlide];
    const Icon = slide.icon;

    const getColorClass = (color: string, type: 'bg' | 'text' | 'border') => {
        const colors: Record<string, Record<string, string>> = {
            emerald: { bg: 'bg-emerald-500', text: 'text-emerald-400', border: 'border-emerald-500' },
            sky: { bg: 'bg-sky-500', text: 'text-sky-400', border: 'border-sky-500' },
            amber: { bg: 'bg-amber-500', text: 'text-amber-400', border: 'border-amber-500' },
            indigo: { bg: 'bg-indigo-500', text: 'text-indigo-400', border: 'border-indigo-500' },
        };
        return colors[color][type];
    };

    return (
        <div aria-label="Page Container" className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 text-gray-800 flex flex-col">
            {/* Skip Button */}
            <div className="absolute top-6 right-6 z-20">
                <button
                    onClick={handleSkip}
                    className="px-5 py-2.5 text-base text-gray-700 hover:text-emerald-600 transition-colors font-semibold bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200"
                >
                    Skip
                </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
                {/* Logo */}
                <div className="mb-12">
                    <Logo size={80} animated />
                </div>

                {/* Slide Content */}
                <div
                    key={slide.id}
                    className="text-center animate-in fade-in slide-in-from-right-8 duration-500"
                >
                    {/* Icon */}
                    <div className={`w-28 h-28 rounded-3xl ${getColorClass(slide.color, 'bg')}/20 flex items-center justify-center mx-auto mb-8 border-2 ${getColorClass(slide.color, 'border')}/30`}>
                        <Icon size={56} className={getColorClass(slide.color, 'text')} />
                    </div>

                    {/* Title */}
                    <h1 className="text-3xl font-bold mb-2">{slide.title}</h1>
                    <p className={`text-xl font-hindi ${getColorClass(slide.color, 'text')} mb-6`}>
                        {slide.titleHi}
                    </p>

                    {/* Description */}
                    <p className="text-lg text-gray-600 mb-3 max-w-sm mx-auto leading-relaxed">
                        {slide.description}
                    </p>
                    <p className="text-base text-gray-500 font-hindi max-w-sm mx-auto">
                        {slide.descHi}
                    </p>
                </div>
            </div>

            {/* Progress Dots */}
            <div className="flex justify-center gap-3 mb-8">
                {ONBOARDING_SLIDES.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide
                            ? `${getColorClass(slide.color, 'bg')} w-8`
                            : 'bg-gray-300 hover:bg-gray-400'
                            }`}
                    />
                ))}
            </div>

            {/* Next Button */}
            <div className="px-8 pb-12">
                <button
                    onClick={handleNext}
                    className={`w-full py-5 ${getColorClass(slide.color, 'bg')} text-white rounded-2xl font-bold text-xl flex items-center justify-center gap-3 shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98]`}
                >
                    {currentSlide < ONBOARDING_SLIDES.length - 1 ? (
                        <>
                            Next
                            <ChevronRight size={24} />
                        </>
                    ) : (
                        <>
                            Get Started
                            <ChevronRight size={24} />
                        </>
                    )}
                </button>

                {/* Progress indicator */}
                <p className="text-center text-base text-gray-500 mt-4">
                    {currentSlide + 1} of {ONBOARDING_SLIDES.length}
                </p>
            </div>
        </div>
    );
}

export default OnboardingScreen;
